import React, { useMemo } from 'react';
import { Box, costFormater, Text } from '@common-fe/common-fe';

import { TriggerCategory } from '@/modules/HealthPlan/HealthPlan.types';
import { RenewalDeductibleConfig, RenewalTriggerConfig } from '@/modules/Renewals/renewals.types';
import { formatCategoryType } from '@/utils';

import { PayoutConnectorLine } from '../HealthPlanSummaryCard.styles';

import ExpandableRow from './ExpandableRow';

const getTriggerLabel = (trigger: RenewalTriggerConfig): string => {
  if (trigger.triggerName) {
    return trigger.triggerName;
  }
  if (trigger.triggerCategoryType === TriggerCategory.EMBEDDED) {
    return 'Individual amount';
  }
  if (trigger.triggerCategoryType === TriggerCategory.FAMILY) {
    return 'Family amount';
  }
  return '-';
};

interface Props {
  config: RenewalDeductibleConfig;
  isLast: boolean;
  testId?: string;
}

const CoverageSection: React.FC<Props> = ({ config, isLast, testId }) => {
  const hasTriggers = (config.triggerConfigs || []).length > 0;
  const hasAutoFunding = (config.autoFundingConfigs || []).length > 0;

  const plansData = useMemo(
    () =>
      (config.autoFundingConfigs || []).map((funding) => {
        const planCategoryLimits = (config.categorySpendLimits || []).filter(
          (csl) => csl.planId === funding.planId && csl.categorySpendLimit != null
        );
        const hasSpendLimits = funding.perPersonSpendLimitAmount != null || planCategoryLimits.length > 0;
        const spendLimitsText = hasSpendLimits
          ? [
              funding.perPersonSpendLimitAmount != null
                ? `Per person (${costFormater(funding.perPersonSpendLimitAmount, true)})`
                : null,
              ...planCategoryLimits.map(
                (csl) =>
                  `${formatCategoryType(csl.categoryType || '')} (${costFormater(csl.categorySpendLimit ?? 0, true)})`
              ),
            ]
              .filter(Boolean)
              .join(', ')
          : null;

        return {
          planId: funding.planId,
          planName: funding.planName,
          hasSpendLimits,
          spendLimitsText,
          autoEnrollmentAmount: funding.autoEnrollmentAmount,
        };
      }),
    [config.autoFundingConfigs, config.categorySpendLimits]
  );
  return (
    <ExpandableRow
      titleNode={<Text color="textBody">{config.coverageName}</Text>}
      defaultExpanded={false}
      testId={`${testId}_coverage-${config.coverageCode}`}
      showArrowDown={!isLast}
      isLast={isLast}
      disabled={!hasTriggers}
      allowBottomMargin
    >
      <Box style={{ position: 'relative' }}>
        {/* Single block with connector line (like Lock card purse in TierSection) */}
        <Box
          background="module"
          round="container2Round"
          pad={{ vertical: 'spacing16', horizontal: 'spacing16' }}
          style={{ position: 'relative' }}
        >
          <PayoutConnectorLine />
          <ExpandableRow
            titleNode={
              hasTriggers ? (
                <Box>
                  {(config.triggerConfigs || []).map((trigger, idx) => (
                    <Text key={idx} size="medium" color="textBody">
                      <Text weight={700} color="textBody">
                        {getTriggerLabel(trigger)}:
                      </Text>
                      {` ${costFormater(trigger.thresholdAmount ?? 0, true)}`}
                    </Text>
                  ))}
                </Box>
              ) : undefined
            }
            defaultExpanded
            disabled={!hasTriggers}
            testId={`${testId}_coverage-${config.coverageCode}_triggers`}
            contentLines={config.triggerConfigs?.length}
            // Only draw the curved connector line when there is a Plans block
            // below it to connect to; otherwise it dangles into empty space.
            shouldRenderCurvedLine={hasAutoFunding}
          >
            {/* Plans */}
            {hasAutoFunding && (
              <Box style={{ position: 'relative' }}>
                <Text size="small" color="textSecondary" weight={500} margin={{ bottom: 'spacing4' }}>
                  Plans
                </Text>
                <Box gap="spacing8">
                  {plansData.map((plan) => (
                    <Box
                      key={plan.planId}
                      pad={{ vertical: 'spacing8', horizontal: 'spacing16' }}
                      border={{ size: 'small', color: 'border1' }}
                      round="container2Round"
                      background="canvas"
                    >
                      <Text size="medium" weight={700}>
                        {plan.planName}
                      </Text>
                      {plan.hasSpendLimits && (
                        <Text size="medium" color="textBody">
                          <Text weight={700} color="textBody">
                            Spend limits:
                          </Text>
                          {` ${plan.spendLimitsText}.`}
                        </Text>
                      )}
                      {plan.autoEnrollmentAmount != null && (
                        <Text size="medium" color="textBody">
                          <Text weight={700} color="textBody">
                            Auto-funding amount:
                          </Text>
                          {` ${costFormater(plan.autoEnrollmentAmount, true)}`}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </ExpandableRow>
        </Box>
      </Box>
    </ExpandableRow>
  );
};

export default CoverageSection;
